// File: EtVisualAccountPlan.jsx

import React, { useCallback, useState } from 'react';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  addEdge,
  useEdgesState,
  useNodesState,
  MarkerType,
  Position,
} from 'react-flow-renderer';

import 'react-flow-renderer/dist/style.css';

const initialNodes = [
  {
    id: '1',
    type: 'default',
    position: { x: 250, y: 25 },
    data: { label: 'Global Account' },
    style: { border: '2px solid orange', padding: 10, borderRadius: 10 },
  },
  {
    id: '2',
    type: 'default',
    position: { x: 100, y: 150 },
    data: { label: 'LOB: Application Services' },
    style: { border: '1px dashed gray', padding: 10, borderRadius: 10 },
  },
  {
    id: '3',
    type: 'default',
    position: { x: 400, y: 150 },
    data: { label: 'LOB: Cloud & Infra' },
    style: { border: '1px dashed gray', padding: 10, borderRadius: 10 },
  },
];

const initialEdges = [
  {
    id: 'e1-2',
    source: '1',
    target: '2',
    type: 'straight',
    markerEnd: { type: MarkerType.Arrow },
  },
  {
    id: 'e1-3',
    source: '1',
    target: '3',
    type: 'straight',
    markerEnd: { type: MarkerType.Arrow },
  },
];

const EtVisualAccountPlan = () => {
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  const [newNodeLabel, setNewNodeLabel] = useState('');
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [selectedEdge, setSelectedEdge] = useState(null);

  const onConnect = useCallback(
    (params) =>
      setEdges((eds) =>
        addEdge(
          {
            ...params,
            type: 'step',
            markerEnd: { type: MarkerType.Arrow },
          },
          eds
        )
      ),
    [setEdges]
  );

  const addNode = () => {
    if (!selectedNodeId || !newNodeLabel) return;

    const newId = (nodes.length + 1).toString();
    const parentNode = nodes.find((n) => n.id === selectedNodeId);
    const newY = parentNode.position.y + 130;
    const newX = parentNode.position.x + Math.random() * 150 - 75;

    const newNode = {
      id: newId,
      type: 'default',
      position: { x: newX, y: newY },
      data: { label: newNodeLabel },
      style: { border: '1px dashed gray', padding: 10, borderRadius: 10 },
    };

    setNodes((nds) => [...nds, newNode]);
    setEdges((eds) => [
      ...eds,
      {
        id: `e${selectedNodeId}-${newId}`,
        source: selectedNodeId,
        target: newId,
        type: 'step',
        markerEnd: { type: MarkerType.Arrow },
      },
    ]);

    setNewNodeLabel('');
  };

  return (
    <div className="w-full h-[80vh] bg-gray-50 rounded shadow-md p-2 relative">
      <div className="absolute z-10 top-2 left-2 bg-white shadow p-2 rounded text-xs space-y-2">
        <p className="font-bold text-orange-600">Add Department Node</p>
        <input
          className="border rounded px-2 py-1 text-sm w-full"
          placeholder="Node label"
          value={newNodeLabel}
          onChange={(e) => setNewNodeLabel(e.target.value)}
        />
        <select
          className="border rounded px-2 py-1 text-sm w-full"
          onChange={(e) => setSelectedNodeId(e.target.value)}
        >
          <option value="">Choose parent node</option>
          {nodes.map((n) => (
            <option key={n.id} value={n.id}>
              {n.data.label}
            </option>
          ))}
        </select>
        <button
          className="bg-orange-600 text-white px-2 py-1 rounded w-full text-sm"
          onClick={addNode}
        >
          + Add Node
        </button>
      </div>

      {selectedEdge && (
        <div className="fixed bottom-4 right-4 bg-white shadow-lg p-3 rounded z-50 text-xs">
          <div className="font-semibold mb-2">Edit Connector Type</div>
          <select
            className="border px-2 py-1 text-sm rounded"
            value={selectedEdge.type || 'default'}
            onChange={(e) => {
              const newType = e.target.value;
              setEdges((eds) =>
                eds.map((edge) =>
                  edge.id === selectedEdge.id ? { ...edge, type: newType } : edge
                )
              );
              setSelectedEdge(null);
            }}
          >
            <option value="straight">Straight</option>
            <option value="step">Elbow (Step)</option>
            <option value="bezier">Curved (Bezier)</option>
            <option value="smoothstep">Smooth Elbow</option>
          </select>
          <button
            onClick={() => setSelectedEdge(null)}
            className="ml-2 text-gray-500 underline"
          >
            Cancel
          </button>
        </div>
      )}

      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onEdgeClick={(_, edge) => setSelectedEdge(edge)}
        fitView
      >
        <MiniMap />
        <Controls />
        <Background />
      </ReactFlow>
    </div>
  );
};

export default EtVisualAccountPlan;